import { useEffect, useRef, useState } from "react";

/** Singola immagine della galleria (URL piena risoluzione + nome file). */
export interface LightboxImage {
  /** URL della immagine a piena risoluzione. */
  src: string;
  /** Nome file mostrato in header + usato per il download. */
  fileName: string;
}

interface ImageLightboxProps {
  /** Galleria di immagini navigabili con le frecce. Almeno una. */
  images: LightboxImage[];
  /** Indice dell'immagine mostrata all'apertura (clampato in [0, len-1]). */
  initialIndex?: number;
  /** Chiamato alla chiusura (X, click su backdrop, ESC). */
  onClose: () => void;
}

/**
 * Lightbox immagine fullscreen per la pagina Messaggi, in modalita' GALLERIA.
 *
 * Audit 2026-05-28 (Davide "preview foto in ricezione + invio"): le
 * foto inviate dai clienti marketplace sono il primo segnale visivo di
 * difetto / consegna sbagliata. Aprirle direttamente in una finestra
 * dedicata + zoom edge-to-edge evita di dover scaricarle solo per
 * vederle bene.
 *
 * Richiesta 2026-06-29 (Davide "quando un cliente invia delle foto voglio
 * le freccette per consultarle piu' velocemente"): quando un messaggio ha
 * piu' immagini si naviga prev/next con le frecce a schermo o da tastiera,
 * senza chiudere e riaprire il lightbox. La galleria fa wrap-around (dopo
 * l'ultima si torna alla prima e viceversa) perche' e' il modo piu' rapido
 * di sfogliare poche foto.
 *
 * - Click backdrop OR pulsante chiude
 * - ESC chiude; ArrowLeft = precedente, ArrowRight = successiva
 * - Frecce a schermo solo se ci sono >= 2 immagini; click su freccia/immagine
 *   NON chiude il lightbox
 * - Indicatore posizione "n / tot" in header con il nome file corrente
 * - Bottone "Scarica" usa il flow standard del browser
 *   (``<a href download>``): in Electron il main process configura
 *   ``session.will-download`` per salvare in ``~/Downloads`` senza
 *   prompt Save-As (richiesta Davide). Scarica SEMPRE l'immagine corrente.
 * - Focus trap: focus iniziale sul bottone chiudi cosi' chi naviga da
 *   tastiera puo' chiudere con ENTER.
 */
export function ImageLightbox({ images, initialIndex = 0, onClose }: ImageLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const total = images.length;
  // Clamp difensivo: un initialIndex fuori range non deve mostrare undefined.
  const safeInitial = total > 0 ? Math.min(Math.max(initialIndex, 0), total - 1) : 0;
  const [index, setIndex] = useState(safeInitial);

  const hasGallery = total > 1;
  // Wrap-around con modulo: prev sull'indice 0 va all'ultima, next sull'ultima
  // torna alla prima.
  const goPrev = () => setIndex((i) => (i - 1 + total) % total);
  const goNext = () => setIndex((i) => (i + 1) % total);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (!hasGallery) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setIndex((i) => (i - 1 + total) % total);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setIndex((i) => (i + 1) % total);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, hasGallery, total]);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  // Difesa: nessuna immagine (o indice fuori range) -> non renderizzare il
  // dialog vuoto. Il check su current soddisfa anche noUncheckedIndexedAccess.
  const current = images[index];
  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Anteprima ${current.fileName}`}
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      onClick={(event) => {
        // Click sul backdrop chiude. Click sull'immagine/frecce NO (l'utente
        // potrebbe voler tenere aperta + selezionare o sfogliare).
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="flex items-center justify-between gap-3 px-5 py-3 bg-black/40 text-white">
        <div className="flex min-w-0 items-center gap-2">
          {hasGallery && (
            <span className="shrink-0 rounded-md bg-white/10 px-2 py-0.5 text-[12px] font-mono tabular-nums">
              {index + 1} / {total}
            </span>
          )}
          <span className="truncate text-[13px] font-mono" title={current.fileName}>
            {current.fileName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={current.src}
            download={current.fileName}
            className="rounded-md border border-white/30 bg-white/10 px-3 py-1 text-[12px] text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            Scarica
          </a>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Chiudi anteprima"
            className="rounded-md border border-white/30 bg-white/10 px-3 py-1 text-[12px] text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            Chiudi (Esc)
          </button>
        </div>
      </header>
      <div className="relative flex flex-1 items-center justify-center overflow-auto p-5">
        {hasGallery && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="Foto precedente"
            className="absolute left-3 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/30 bg-white/10 text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <ChevronLeftIcon />
          </button>
        )}
        <img
          src={current.src}
          alt={current.fileName}
          className="max-h-full max-w-full object-contain"
          draggable={false}
        />
        {hasGallery && (
          <button
            type="button"
            onClick={goNext}
            aria-label="Foto successiva"
            className="absolute right-3 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/30 bg-white/10 text-white hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <ChevronRightIcon />
          </button>
        )}
      </div>
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      className="h-6 w-6 fill-none stroke-current stroke-[2]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M15 18 L9 12 L15 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      className="h-6 w-6 fill-none stroke-current stroke-[2]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M9 18 L15 12 L9 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
